// The case change log (ADR-0090, PRD-0075): what the case learned about an entity, newest first.
// Entities are named by their stable `_uid` (ADR-0091), never by a Neo4j element id.
import { apiClient } from './client'

/**
 * The closed vocabulary of the log (`ChangeEventKind` on the server). A reader still takes
 * `kind` as a plain string: a newer server may send a kind this build does not know.
 */
export const CASE_EVENT_KINDS = [
  'source_received',
  'entity_mentioned',
  'relationship_added',
  'relationship_removed',
  'property_changed',
  'entities_merged',
  'merge_reverted',
  'pair_dismissed',
  'fact_asserted',
  'fact_invalidated',
  'entity_retired',
  'source_superseded',
  'assessment_changed',
] as const

export type CaseEventKind = (typeof CASE_EVENT_KINDS)[number]

export function isCaseEventKind(kind: string): kind is CaseEventKind {
  return (CASE_EVENT_KINDS as readonly string[]).includes(kind)
}

/** One event as `GET /v1/cases/{case_id}/events` answers it (`CaseEventOut`). */
export interface CaseEventDto {
  id: string
  kind: string
  subject_uid: string | null
  object_uid: string | null
  document_id: string | null
  /** The document's name at read time; null when the document is gone. */
  document_name: string | null
  /** Where the change came from: `{document_id, chunk_ids, run_id}` for a mention. */
  source_ref: Record<string, unknown>
  /** `system` or a user id. */
  actor: string
  /** When it happened, if the source says; ISO 8601. */
  occurred_at: string | null
  /** When the case learned it; ISO 8601. */
  recorded_at: string
  /** Kind-specific detail: `{label, name}` for a mention. */
  payload: Record<string, unknown>
}

export interface CaseEventsResponse {
  events: CaseEventDto[]
}

/** The server's default page; it accepts 1..500. */
export const CASE_EVENTS_DEFAULT_LIMIT = 50

export const caseEventsApi = {
  /** An entity's events, newest first by `recorded_at`; with `asOf` (*Spis k dátumu*) those learned by then. */
  listForEntity: (
    caseId: string,
    uid: string,
    options: { limit?: number; asOf?: string; signal?: AbortSignal } = {}
  ) =>
    apiClient.get<CaseEventsResponse>(`/v1/cases/${caseId}/events`, {
      params: { uid, limit: options.limit ?? CASE_EVENTS_DEFAULT_LIMIT, as_of: options.asOf },
      signal: options.signal,
    }),
}

/** Mentions the case learned in one calendar month (UTC). */
export interface ActivityMonthDto {
  /** `YYYY-MM`. */
  month: string
  count: number
}

/** A property whose current value replaced a value from another source (ADR-0093). */
export interface PropertyConflictDto {
  key: string
  value: unknown
  previous: unknown
  document_id: string | null
  document_name: string | null
  previous_document_id: string | null
  previous_document_name: string | null
  recorded_at: string
  /** `value` is an analyst's assertion and `previous` the source's value its pin kept out. */
  pinned?: boolean
}

/** One entity as `GET /v1/cases/{case_id}/events/summary` answers it (`EntitySummaryOut`). */
export interface EntitySummaryDto {
  uid: string
  first_seen_at: string | null
  last_seen_at: string | null
  /** Distinct documents that mention the entity. */
  source_count: number
  /** Mentions per month, oldest first; months without any are left out. */
  activity: ActivityMonthDto[]
  /** The newest merge or property change, by learned date. */
  last_change_at: string | null
  last_change: CaseEventDto | null
  conflicts: PropertyConflictDto[]
}

export interface EntitySummariesResponse {
  summaries: EntitySummaryDto[]
}

/** Uids one summary read takes at most (`MAX_SUMMARY_UIDS` on the server). */
export const ENTITY_SUMMARY_MAX_UIDS = 200

export const caseEventSummariesApi = {
  /** Per entity: first/last seen, sources, monthly mentions, last change, conflicts; in the order asked. */
  summaries: (
    caseId: string,
    uids: readonly string[],
    options: { asOf?: string; signal?: AbortSignal } = {}
  ) =>
    apiClient.get<EntitySummariesResponse>(`/v1/cases/${caseId}/events/summary`, {
      params: { uids: uids.join(','), as_of: options.asOf },
      signal: options.signal,
    }),
}
